import { useEffect, useState, useRef, lazy, Suspense } from 'react';
// Patch AX: Vercel Web Analytics. Imported at the top so the script
// loads on every page (including the sign-in screen) before any
// gating logic decides whether to render the app. The <Analytics />
// component itself renders no visible DOM; it just injects the
// tracker script and pushes navigation events.
import { Analytics } from '@vercel/analytics/react';
import { useAppStore } from './state/store.js';
import { useAuthStore } from './state/auth.js';
import { AuthGate } from './components/AuthGate.js';
import { LandingPage } from './components/LandingPage.js';
import { SilkBackground } from './components/SilkBackground.js';
import { isApexDomain, getCurrentSubdomain, isFIRETenant } from './io/tenant.js';
import { MobileNav, useIsMobile, type AppView } from './components/MobileNav.js';
import { onSyncStatus, type SyncStatus } from './io/supabase/sync.js';
import { cloudEnabled } from './io/supabase/client.js';
import { Dashboard } from './pages/Dashboard.js';
import { PurchaseLog } from './pages/PurchaseLog.js';
import { PageErrorBoundary } from './components/PageErrorBoundary.js';

const SalaryPlan = lazy(() => import('./pages/SalaryPlan.js').then(m => ({ default: m.SalaryPlan })));
const LoopLoan = lazy(() => import('./pages/LoopLoan.js').then(m => ({ default: m.LoopLoan })));
const CarryRetirement = lazy(() => import('./pages/CarryRetirement.js').then(m => ({ default: m.CarryRetirement })));
const Assumptions = lazy(() => import('./pages/Assumptions.js').then(m => ({ default: m.Assumptions })));
// PRISM: paul-only tab (stackbtc.net personal instance). Lazy like the other
// non-core pages; it only ever mounts on the paul tenant.
const Prism = lazy(() => import('./pages/Prism.js').then(m => ({ default: m.Prism })));
const News = lazy(() => import('./pages/News.js').then(m => ({ default: m.News })));
const Security = lazy(() => import('./pages/Security.js').then(m => ({ default: m.Security })));
// Patch W: Relocate tab. Lazy because the Cities dataset is ~80KB and
// the leaflet map runtime adds another ~200KB; users who never open
// this tab don't pay the cost.
const Relocate = lazy(() => import('./pages/Relocate.js').then(m => ({ default: m.Relocate })));
// Patch AM: AdminPanel. Lazy because the average user (role==='standard')
// will never open it; super-admin and admins pay the load on first open
// rather than on every page boot.
const AdminPanel = lazy(() => import('./pages/AdminPanel.js').then(m => ({ default: m.AdminPanel })));
// Patch AN: MyPlan tab. Lazy because most users won't have plans uploaded
// to them at any given moment; the iframe + signed-URL machinery only
// needs to load when they actually open the tab. Also keeps the boot
// bundle small for the typical session that never touches this surface.
const MyPlan = lazy(() => import('./pages/MyPlan.js').then(m => ({ default: m.MyPlan })));

// Patch AG: super-admin gate. Only this account auto-loads the workbook
// seed (Paul's actual purchases, scenarios, Bain consulting ladder), and
// only this account sees the "reset to seed" footer button. Other users
// start with engine defaults (empty purchases/scenarios and the generic
// 3-tier W2 salary ladder from defaultSalaryPlan), which they fill in
// manually. Centralized here as the canonical reference; if the address
// ever changes, update this single constant.
//
// Patch AM: also duplicated in src/pages/AdminPanel.tsx (UX gate) and
// api/admin.ts (server-side security boundary). Drift between the three
// would just look weird in the UI; the api/admin.ts copy is what
// actually enforces super-admin status.
const SUPER_ADMIN_EMAIL = 'paul.j.reuer@gmail.com';

// Patch AJ: how long the welcome toast lingers before it auto-fades.
// Long enough to read the message and click the CTA without panic, short
// enough that it doesn't overstay if the user starts navigating around.
const WELCOME_AUTO_DISMISS_MS = 10_000;
// Duration of the opacity transition. Used both for the entrance fade-in
// and the exit fade-out; the persisted welcomed=true is set after the
// exit transition completes so the toast doesn't pop back during fade.
const WELCOME_FADE_MS = 300;

function PageFallback(): JSX.Element {
  return (
    <section className="section">
      <p className="muted mono" style={{ fontSize: 'var(--fs-small)' }}>loading...</p>
    </section>
  );
}

interface ProspectLinks {
  label: string;
  youtube?: string;
  x?: string;
  substack?: string;
  linkedin?: string;
  website?: string;
  facebook?: string;
  coaching?: string;
}

const PROSPECT_CONFIGS: Record<string, ProspectLinks> = {
  rajat: {
    label:    'Rajat Soni',
    youtube:  'https://www.youtube.com/@rajatsonifinance',
    x:        'https://x.com/Rajatsoni',
    substack: 'https://rajatsonifinance.substack.com',
    linkedin: 'https://www.linkedin.com/in/rsoni/',
  },
  bradlong: {
    label:    'Brad Long',
    youtube:  'https://www.youtube.com/@BradLongCo',
    x:        'https://twitter.com/bradlongco',
    linkedin: 'https://www.linkedin.com/in/reformedblong/',
    website:  'https://bradlong.carrd.co/',
  },
  trey: {
    label:    'Trey Sellers',
    youtube:  'https://www.youtube.com/@tsellers33',
    x:        'https://x.com/ts_hodl',
    substack: 'https://www.firebtc.io',
    linkedin: 'https://www.linkedin.com/in/treysellers/',
    website:  'https://www.treysellers.com',
  },
  tim: {
    label:   'Tim Talks Finance',
    youtube: 'https://www.youtube.com/@timtalksfinance',
    x:       'https://x.com/talks_finance',
    website: 'https://timtalksfinance.com',
  },
  private: {
    label: 'Private',
  },
  paul: {
    label: 'Paul Reuer',
  },
};

function ProspectLink(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const subdomain = getCurrentSubdomain();
  const config = subdomain ? PROSPECT_CONFIGS[subdomain] : undefined;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!config) return null;

  if (subdomain === 'private' || subdomain === 'paul') {
    return <span className="prospect-link" style={{ cursor: 'default' }}>By Paul Reuer</span>;
  }

  const closeAndFollow = (): void => setOpen(false);

  return (
    <div ref={wrapRef} className="prospect-wrap">
      <button
        className="prospect-link"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        By {config.label} <span className="caret">{open ? '\u25B2' : '\u25BE'}</span>
      </button>
      {open ? (
        <div className="prospect-dropdown" role="menu">
          {config.youtube  ? <a href={config.youtube}  target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>YouTube</a>  : null}
          {config.x        ? <a href={config.x}        target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>X</a>        : null}
          {config.substack ? <a href={config.substack} target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>Substack</a> : null}
          {config.linkedin ? <a href={config.linkedin} target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>LinkedIn</a> : null}
          {config.coaching ? <a href={config.coaching} target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>Coaching</a> : null}
          {config.facebook ? <a href={config.facebook} target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>Facebook</a> : null}
          {config.website  ? <a href={config.website}  target="_blank" rel="noopener noreferrer" onClick={closeAndFollow}>Website</a>  : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Patch AJ: first-sign-in welcome toast.
 *
 * Shows once for any account that has no purchases yet AND hasn't dismissed
 * before. Two ways it disappears:
 *   - User clicks "Go to Positions" (navigates AND persists welcomed=true)
 *   - User clicks the X (just persists welcomed=true)
 *   - 10-second timer auto-fades and persists welcomed=true
 *
 * The fade-in/fade-out uses a CSS opacity transition rather than mounting
 * and unmounting, so the user sees a smooth dismissal rather than a hard
 * pop. Persisting welcomed=true is deferred until AFTER the fade-out
 * transition completes so the component doesn't unmount mid-fade.
 *
 * Hidden state machine: `mounted` triggers the entrance fade (we set it on
 * a 0-tick timeout so the initial render has opacity 0 and the next render
 * has opacity 1, giving CSS a transition to animate). `visible` controls
 * the exit fade. set to false on dismiss and the opacity goes back to 0,
 * with the welcomed-persist scheduled after WELCOME_FADE_MS.
 */
function WelcomeToast(): JSX.Element | null {
  const purchases = useAppStore(s => s.purchases);
  const welcomed = useAppStore(s => s.welcomed);
  const setWelcomed = useAppStore(s => s.setWelcomed);
  const setView = useAppStore(s => s.setView);
  // Snapshot the should-show condition once on mount so a state change
  // mid-fade (e.g. cloud sync arrives, applyPersistedState lands a new
  // welcomed value) doesn't yank the toast out of the DOM during its
  // exit transition. The component handles its own lifecycle from there.
  const [active] = useState<boolean>(() => !welcomed && purchases.length === 0);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(true);

  // Trigger the entrance fade. setting `mounted` after a 0-tick lets the
  // initial render commit with opacity 0, then the re-render flips to
  // opacity 1 and CSS transitions between them.
  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => setMounted(true), 50);
    return () => window.clearTimeout(t);
  }, [active]);

  // Auto-fade timer. Independent of mount status so the timer also runs
  // through the entrance fade. Total visible time is roughly
  // WELCOME_AUTO_DISMISS_MS regardless of when the user looks at it.
  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(() => setWelcomed(true), WELCOME_FADE_MS);
    }, WELCOME_AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [active, setWelcomed]);

  if (!active) return null;

  const dismiss = (): void => {
    setVisible(false);
    window.setTimeout(() => setWelcomed(true), WELCOME_FADE_MS);
  };

  const goToPurchases = (): void => {
    setView('purchases');
    dismiss();
  };

  const opacity = mounted && visible ? 1 : 0;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        // Sits below the app header. The header height varies a touch
        // between desktop and mobile; 80px clears the desktop hero and
        // mobile nav reliably.
        top: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        maxWidth: 'min(520px, calc(100vw - 32px))',
        background: 'var(--bg-elev)',
        border: '1px solid var(--rule-strong)',
        borderLeft: '3px solid var(--accent)',
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
        opacity,
        transition: `opacity ${WELCOME_FADE_MS}ms ease`,
        // Pointer events disabled while invisible, otherwise the dismissed
        // toast still intercepts clicks during its fade-out.
        pointerEvents: opacity === 0 ? 'none' : 'auto',
      }}
    >
      <div style={{ flex: '1 1 220px', fontSize: 'var(--fs-small)', lineHeight: 1.5 }}>
        {isFIRETenant()
          ? 'Welcome to stackbtc. Track your stack, project your income, and model your Bitcoin path to FI.'
          : 'Welcome to stackbtc. Enter your positions to start tracking.'}
      </div>
      <button
        onClick={goToPurchases}
        className="primary"
        style={{ padding: '6px 12px', fontSize: 'var(--fs-micro)' }}
      >
        Go to Positions
      </button>
      <button
        onClick={dismiss}
        className="ghost"
        style={{ padding: '4px 8px', fontSize: 'var(--fs-micro)' }}
        aria-label="Dismiss welcome message"
        title="Dismiss"
      >
        {'\u00D7'}
      </button>
    </div>
  );
}

export function App(): JSX.Element {
  const view = useAppStore(s => s.view);
  const setView = useAppStore(s => s.setView);
  const isMobile = useIsMobile();
  const purchases = useAppStore(s => s.purchases);
  const lastSeedLoadedAt = useAppStore(s => s.lastSeedLoadedAt);
  const liveStatus = useAppStore(s => s.liveStatus);
  const liveSnapshot = useAppStore(s => s.liveSnapshot);
  const loadFromSeed = useAppStore(s => s.loadFromSeed);
  const resetToSeed = useAppStore(s => s.resetToSeed);
  const exportToJson = useAppStore(s => s.exportToJson);
  const importFromJson = useAppStore(s => s.importFromJson);
  // Patch AS: view-as session state. viewingAs is the {userId,email} of
  // the cohort member currently being browsed, or null in normal mode.
  // exitViewAs restores the admin's snapshotted state and clears the
  // sync gate. Banner + Admin-button-becomes-Exit both branch on this.
  const viewingAs = useAppStore(s => s.viewingAs);
  const exitViewAs = useAppStore(s => s.exitViewAs);

  const undo = useAppStore(s => s.undo);
  const undoStackLength = useAppStore(s => s.undoStack.length);

  // Patch BB: auto-buy walker subscription. Pulled here so the effect
  // below can dep on a stable function reference. Walker is idempotent
  // and exits cheaply when there's no schedule with a passed scheduled
  // date, so re-running on every liveSnapshot change is harmless.
  const runAutoBuyWalker = useAppStore(s => s.runAutoBuyWalker);

  const userEmail = useAuthStore(s => s.user?.email ?? null);
  const signOut = useAuthStore(s => s.signOut);
  // Patch AM: subscribe to role for admin-tab gating. role lands a beat
  // after sign-in (one extra DB read in auth.ts init), so the Admin nav
  // button appears as soon as auth resolves rather than at boot. Treating
  // null as "not-yet-known therefore not admin" is the safe direction:
  // a momentary missed tab is fine, an erroneously visible tab is not.
  const role = useAuthStore(s => s.role);

  // Patch AG: derived flags for super-admin gating. The `noAuth` branch
  // covers local dev where Supabase env vars are missing. In that mode
  // there's no signed-in user at all, so we fall back to the legacy
  // "always seed" behavior so the dev still gets a populated app to
  // poke at. Production always has cloudEnabled() === true, so the
  // production-relevant condition is just isSuperAdmin.
  const isSuperAdmin = userEmail === SUPER_ADMIN_EMAIL;
  const noAuth = !cloudEnabled();
  const canSeedReset = isSuperAdmin || noAuth;
  // Patch AM: combined admin flag. Super-admin has admin powers
  // implicitly (gate enforced server-side too); other admins are
  // promoted by super-admin via /api/admin and have role==='admin' on
  // their app_state row. Either path lights up the Admin tab.
  const isAdmin = isSuperAdmin || role === 'admin';
  // PRISM is a paul-only tab. The stackbtc.net personal instance sets
  // VITE_TENANT_SLUG=paul, so getCurrentSubdomain() resolves to 'paul' there
  // and to a real subdomain (or null) on every prospect tenant.
  const isPaul = getCurrentSubdomain() === 'paul';

  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [syncErrMsg, setSyncErrMsg] = useState<string | null>(null);
  const [syncErrCount, setSyncErrCount] = useState(0);
  useEffect(() => onSyncStatus((s, e) => {
    setSyncStatus(s);
    setSyncErrMsg(e ? e.message : null);
    if (s === 'error') setSyncErrCount(c => c + 1);
    if (s === 'saved') setSyncErrCount(0);
  }), []);

  // Patch AO: auto-seed restricted to noAuth mode (local dev / tests).
  // Pre-AO this also ran when isSuperAdmin && cloudEnabled, but that
  // path was the wipe foot-gun: AuthGate calls resetToDefaults around
  // sign-in (clearing lastSeedLoadedAt to null), then loads cloud
  // asynchronously, and during that gap this effect's condition
  // (purchases.length === 0 && lastSeedLoadedAt === null) would fire
  // loadFromSeed and overwrite real cloud data with the workbook seed.
  // Paul lost manually-entered purchases to exactly this race. The
  // noAuth branch has no AuthGate / no cloud-load to race, so it stays;
  // useful for local dev and the smoke tests that expect the seed to
  // populate on mount. The "reset to seed" footer button still does
  // the same thing for super-admin in production via explicit click.
  useEffect(() => {
    if (cloudEnabled()) return;
    if (purchases.length === 0 && lastSeedLoadedAt === null) {
      void loadFromSeed().catch(err => {
        console.warn('Seed auto-load failed:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // Patch AM: bounce off the admin tab if the user lost admin rights
  // (e.g. the super-admin demoted them while their session was open).
  // Without this, the page would render the AdminPanel component which
  // would then show its standard-user placeholder, but the active nav
  // button would still highlight Admin which would look broken.
  // Redirecting to dashboard is the conservative choice; the user can
  // always click back if they wanted to see the placeholder.
  useEffect(() => {
    if (view === 'admin' && !isAdmin) {
      setView('dashboard');
    }
  }, [view, isAdmin, setView]);

  // Same guard for PRISM: it only exists on the paul tenant. The view isn't
  // persisted, but if a 'prism' value ever surfaces on another tenant, bounce
  // to the dashboard rather than render an empty gated-out main.
  useEffect(() => {
    if (view === 'prism' && !isPaul) {
      setView('dashboard');
    }
  }, [view, isPaul, setView]);

  // Track which views have ever been rendered so we can keep them mounted
  // after the first visit instead of unmounting on every tab switch. A ref
  // is sufficient: the view change already triggers a re-render via the
  // store subscription, so we don't need extra state here. 'powerlaw' is
  // an alias for 'assumptions' (Patch BR), so we normalize it.
  const mountedViewsRef = useRef<Set<string>>(new Set());
  const effectiveView = view === 'powerlaw' ? 'assumptions' : view;
  mountedViewsRef.current.add(effectiveView);
  // Admin panel is only mounted if the user is (or was) admin.
  if (isAdmin) mountedViewsRef.current.add('admin');

  // Patch BB: fire the auto-buy walker whenever liveSnapshot changes.
  // The walker is idempotent: re-running with the same lastPopulatedDate
  // emits no rows. So the cost of running on every snapshot refresh
  // (which happens every few minutes via the price-fetch poller) is one
  // map lookup per schedule and an early-return. The benefit is that:
  //   1. The first walker run happens once BTC + ETF prices are loaded,
  //      not on cold boot when priceMap would be empty and every
  //      schedule would defer.
  //   2. If the user keeps the tab open past midnight UTC, the next
  //      price refresh after midnight fires the walker and creates the
  //      new day's auto-buy rows automatically.
  //   3. After a price-refresh that fills in a previously-missing ETF
  //      price, schedules for that ETF resume populating.
  useEffect(() => {
    if (liveSnapshot) runAutoBuyWalker();
  }, [liveSnapshot, runAutoBuyWalker]);

  // Patch BE: populate the Ledn snapshot on app boot. Ledn rates are
  // hardcoded in src/io/live/ledn.ts (no public API to call), so this
  // is effectively a synchronous read wrapped in a Promise for shape-
  // parity with the Aave path. We fire it from here, not from
  // LoopLoan's useAutoRefresh, so the snapshot is available on every
  // page (Purchases / Carry / Salary) even before the user visits
  // Loop Loan - which is what made the pre-BE "Active APR shows 6%"
  // bug visible on Purchases.
  //
  // Runs once on mount. Re-fetch is triggered from LoopLoan's Refresh
  // button or by switching provider; we don't need a periodic poll
  // because the underlying constants only change when code changes.
  const setLednSnapshot = useAppStore(s => s.setLednSnapshot);
  const setLednStatus = useAppStore(s => s.setLednStatus);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Lazy import keeps the Ledn module off the initial bundle's
        // critical path; the dynamic import resolves from the prebuilt
        // chunk on subsequent calls.
        const { fetchLednBorrow } = await import('./io/live/ledn.js');
        setLednStatus('loading');
        const snap = await fetchLednBorrow();
        if (cancelled) return;
        setLednSnapshot(snap);
        setLednStatus('ok');
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setLednStatus('error', msg);
      }
    })();
    return () => { cancelled = true; };
  }, [setLednSnapshot, setLednStatus]);

  // Patch BF: Aave boot-fetch. Pre-BF, the Aave snapshot only loaded via
  // LoopLoan's useAutoRefresh, so a user who landed on Purchases (or
  // anywhere other than LoopLoan) first saw aaveSnapshot===null until
  // they visited LoopLoan. That made the Active APR tile, dashboard
  // loan callout, and effectiveBorrowApr derivation all fall back to
  // Ledn (or 6%), even though the user's intended provider was Aave.
  //
  // Same one-shot pattern as the Ledn effect: fire once on mount, read
  // the defillamaQuery via getState (not a subscription) so we don't
  // re-fetch when the query changes - LoopLoan's useAutoRefresh handles
  // that case when the user is actually looking at the panel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { DefiLlamaProvider } = await import('./io/live/defillama.js');
        const provider = new DefiLlamaProvider();
        const { defillamaQuery, setAaveStatus, setAaveSnapshot } = useAppStore.getState();
        setAaveStatus('loading');
        const snap = await provider.fetchUsdcBorrowApr(defillamaQuery);
        if (cancelled) return;
        setAaveSnapshot(snap);
        setAaveStatus('ok');
      } catch (e) {
        if (cancelled) return;
        const { setAaveStatus } = useAppStore.getState();
        const msg = e instanceof Error ? e.message : String(e);
        setAaveStatus('error', msg);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Patch BQ: boot-fetch loop for ETF holdings + security assets.
  //
  // Pre-BQ, prices on existing assets were only refreshed on three
  // events: (1) the asset was just added (one-shot fetch fires from
  // the store action), (2) the user manually clicked a per-row refresh
  // button, (3) the dashboard fired its BTC price refresh (which only
  // updates BTC, not the other assets). Result: open the app fresh,
  // every non-BTC asset shows its last-saved price snapshot, which can
  // be hours/days/weeks old.
  //
  // This effect fires once on app mount and refreshes ALL of them in
  // parallel via Promise.allSettled so one bad symbol doesn't block
  // the rest. Reads from the store via getState() to avoid re-running
  // when the store changes (we only want this on mount).
  //
  // The edge function has a 5-minute in-memory cache (also Patch BQ),
  // so consecutive page reloads within 5 minutes hit cached responses
  // for each symbol - very cheap on the network side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { etfHoldings, securityAssets } = useAppStore.getState();
      if (etfHoldings.length === 0 && securityAssets.length === 0) return;
      const { fetchEtfPrice } = await import('./io/live/etf-price.js');
      if (cancelled) return;
      const tasks: Array<Promise<void>> = [];
      for (const h of etfHoldings) {
        tasks.push((async () => {
          try {
            const snap = await fetchEtfPrice(h.symbol);
            if (cancelled) return;
            // Patch DX: setEtfLivePrice instead of updateEtfHolding so
            // the boot-time price fetch doesn't fire a cloud save. The
            // holding itself hasn't changed; only the transient live
            // snapshot has.
            useAppStore.getState().setEtfLivePrice(h.id, snap);
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            console.warn(`[boot-fetch] ETF holding ${h.symbol}:`, msg);
          }
        })());
      }
      for (const a of securityAssets) {
        tasks.push((async () => {
          try {
            const snap = await fetchEtfPrice(a.symbol);
            if (cancelled) return;
            // Patch DX: see ETF block above. setSecurityLivePrice
            // bypasses saveToStorage so the boot price fetch is silent.
            useAppStore.getState().setSecurityLivePrice(a.id, {
              priceUsd: snap.priceUsd, fetchedAt: new Date(),
            });
          } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            console.warn(`[boot-fetch] security asset ${a.symbol}:`, msg);
          }
        })());
      }
      await Promise.allSettled(tasks);
    })();
    return () => { cancelled = true; };
  }, []);

  // On the bare apex domain (stackbtc.net), render only the landing card.
  // Prospects access their personalized version via their assigned subdomain
  // (e.g. rajat.stackbtc.net). This keeps the customized content off the
  // public-facing root URL.
  //
  // Only active when cloud is configured (production). In offline dev or
  // JSDOM tests where Supabase env vars are absent, the full app renders
  // regardless of hostname so local testing is unaffected.
  if (cloudEnabled() && isApexDomain()) {
    return <LandingPage />;
  }

  const handleImport = (): void => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      try {
        importFromJson(text);
        alert(`Imported ${file.name} successfully.`);
      } catch (e) {
        alert(`Import failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    };
    input.click();
  };

  const liveStatusClass =
    liveStatus === 'ok' ? '' :
    liveStatus === 'error' ? 'error' :
    'stale';
  const liveStatusText =
    liveStatus === 'ok' && liveSnapshot
      ? `live - ${liveSnapshot.fetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : liveStatus === 'error'
        ? 'live error'
        : liveStatus === 'loading'
          ? 'fetching...'
          : 'manual';

  const syncText =
    syncStatus === 'saving' ? 'saving...' :
    syncStatus === 'saved'  ? 'synced' :
    syncStatus === 'error' && syncErrCount <= 1
      ? `sync error: ${(syncErrMsg ?? 'unknown').slice(0, 60)}` :
    syncStatus === 'error'
      ? 'sync error, retrying automatically' :
    null;

  const canUndo = undoStackLength > 0;

  // Patch AX: <Analytics /> is rendered as a sibling of <AuthGate>, NOT
  // a child, so the tracker mounts and starts collecting page-view
  // events even before the user signs in. AuthGate's render-lock would
  // suppress the tracker on the sign-in screen otherwise, which would
  // hide the most interesting funnel datapoint (people who land on the
  // app but never authenticate). The component itself renders no DOM;
  // it just injects a script tag and emits navigation pings.
  return (
    <>
      <Analytics />
      <SilkBackground />
      <AuthGate>
        <div className="app" data-purchases={purchases.length}>
          {/* Patch AS: view-as banner. Rendered as the very first child of
              the app shell so it's visually anchored above all content
              regardless of which page the admin navigates to inside the
              session. The amber accent borrows the existing --warn token
              instead of inventing a new one, keeping color-system entropy
              low. The Exit button restores the admin snapshot and bounces
              them back to the Admin tab; we also flip view BEFORE calling
              exitViewAs so the render after restoration lands on a
              sensible page rather than (e.g.) the LoopLoan tab now
              showing the admin's own data. */}
          {viewingAs ? (
            <div
              role="status"
              style={{
                background: 'var(--accent-soft, rgba(199,107,31,0.12))',
                border: '1px solid var(--accent, #C76B1F)',
                borderRadius: 4,
                padding: '8px 14px',
                margin: '0 0 var(--gap-m) 0',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--gap-s)',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 'var(--fs-small)', fontWeight: 600 }}>
                Viewing {viewingAs.email}
              </span>
              <span className="muted" style={{ fontSize: 'var(--fs-micro)' }}>
                Read-only. Edits made here do not save to anyone.
              </span>
              <button
                onClick={() => { setView('admin'); exitViewAs(); }}
                className="primary"
                style={{ padding: '4px 12px', fontSize: 'var(--fs-micro)', marginLeft: 'auto' }}
                title="Restore your own data and return to the Admin tab"
              >
                Exit view
              </button>
            </div>
          ) : null}
          {/* Patch AJ: welcome toast. Renders inside AuthGate so it only
              appears post-sign-in (children are gated by AH's render guard);
              the toast handles its own visibility based on the welcomed flag
              and purchases-emptiness so it stays out of the way for users
              who have data already. */}
          <WelcomeToast />
          <header className="app-header">
            <div className="brand">
              <div className="brand-name">stackbtc</div>
              <ProspectLink />
              {getCurrentSubdomain() !== 'private' && getCurrentSubdomain() !== 'paul' ? <div className="brand-paul">and Paul Reuer</div> : null}
            </div>
            <nav>
              {isMobile ? (
                <MobileNav
                  view={view as AppView}
                  setView={(v) => setView(v)}
                  isAdmin={isAdmin}
                  labelOverrides={isFIRETenant()
                    ? { salaryPlan: 'Income', carry: 'FIRE', myPlan: 'My FIRE Plan' }
                    : {}}
                />
              ) : (
                <>
                  <button className={view === 'dashboard' ? 'active' : ''} onClick={() => setView('dashboard')}>
                    Dashboard
                  </button>
                  <button className={view === 'purchases' ? 'active' : ''} onClick={() => setView('purchases')}>
                    Positions
                  </button>
                  <button className={view === 'salaryPlan' ? 'active' : ''} onClick={() => setView('salaryPlan')}>
                    {isFIRETenant() ? 'Income' : 'Career'}
                  </button>
                  <button className={view === 'loopLoan' ? 'active' : ''} onClick={() => setView('loopLoan')}>
                    Leverage
                  </button>
                  <button className={view === 'carry' ? 'active' : ''} onClick={() => setView('carry')}>
                    {isFIRETenant() ? 'FIRE' : 'Retire'}
                  </button>
                  {/* Patch BR: Power Law tab removed; its content now lives
                      inside Assumptions. The 'powerlaw' view value is still
                      handled in the route case below (redirects to Assumptions)
                      so persisted state from before BR doesn't break. */}
                  <button className={view === 'relocate' ? 'active' : ''} onClick={() => setView('relocate')}>
                    Relocate
                  </button>
                  <button className={view === 'security' ? 'active' : ''} onClick={() => setView('security')}>
                    Security
                  </button>
                  <button className={view === 'news' ? 'active' : ''} onClick={() => setView('news')}>
                    News
                  </button>
                  <button className={view === 'assumptions' ? 'active' : ''} onClick={() => setView('assumptions')}>
                    Assumptions
                  </button>
                  {/* PRISM: paul-only tab (stackbtc.net personal instance). */}
                  {isPaul ? (
                    <button className={view === 'prism' ? 'active' : ''} onClick={() => setView('prism')}>
                      PRISM
                    </button>
                  ) : null}
                  {/* Patch AN: My Plan tab, visible to everyone signed in.
                      Placed between Assumptions and Admin so the rightmost
                      slot stays the privileged Admin tab. The MyPlan
                      component itself handles the empty-state case (no
                      plans uploaded yet) so we don't need a visibility
                      gate here; an unaffiliated user just sees a friendly
                      "no plans yet" message rather than a missing tab. */}
                  <button className={view === 'myPlan' ? 'active' : ''} onClick={() => setView('myPlan')}>
                    {isFIRETenant() ? 'My FIRE Plan' : 'My Plan'}
                  </button>
                  {/* Patch AM: admin tab, last in the row (rightmost). Only
                      rendered when the auth-resolved isAdmin flag is true.
                      Because role lands one render after status flips to
                      'signed-in', the tab fades in shortly after the rest
                      of the nav, which is fine: the AuthGate render lock
                      means the user isn't even seeing the app until cloud
                      state has loaded, by which point role has populated.

                      Patch AS: while viewing-as, this slot becomes the
                      Exit-view affordance instead. Keeps the rightmost
                      position consistent so the admin's muscle memory
                      works either way, AND removes the temptation to
                      click Admin and end up looking at the roster while
                      the store still holds client data. */}
                  {isAdmin ? (
                    viewingAs ? (
                      <button
                        onClick={() => { setView('admin'); exitViewAs(); }}
                        title="Exit the read-only view and return to Admin"
                      >
                        Exit view
                      </button>
                    ) : (
                      <button className={view === 'admin' ? 'active' : ''} onClick={() => setView('admin')}>
                        Admin
                      </button>
                    )
                  ) : null}
                </>
              )}
            </nav>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-s)' }}>
              <button
                onClick={() => undo()}
                disabled={!canUndo}
                title={canUndo ? `Undo last edit (${undoStackLength} step${undoStackLength === 1 ? '' : 's'} available)` : 'Nothing to undo'}
                style={{
                  fontSize: 'var(--fs-micro)',
                  padding: '4px 10px',
                  opacity: canUndo ? 1 : 0.45,
                  cursor: canUndo ? 'pointer' : 'default',
                }}
              >
                {'\u21B6'} Undo
              </button>
              {cloudEnabled() && syncText ? (
                <span style={{ fontSize: 'var(--fs-micro)', color: syncStatus === 'error' ? 'var(--neg)' : 'var(--text-faint)', fontFamily: 'var(--mono)' }}>{syncText}</span>
              ) : null}
              <div className={`live-status ${liveStatusClass}`}>
                <span className="dot" />
                {liveStatusText}
              </div>
              {userEmail ? (
                <button
                  onClick={() => { void signOut(); }}
                  title={userEmail}
                  style={{ fontSize: 'var(--fs-micro)', padding: '4px 8px' }}
                >
                  Sign out
                </button>
              ) : null}
            </div>
          </header>

          <main className="app-main">
            {/* Pages stay mounted after first visit; only their visibility
                wrapper toggles. This eliminates the per-tab-switch remount
                cost (lazy chunk reload, full memo recomputation, fresh
                useAutoRefresh fetches). Dashboard + PurchaseLog are always
                mounted immediately (non-lazy, cheap to init). Lazy pages
                mount on first visit and stay mounted afterward. */}

            {/* ---- always-mounted, non-lazy ---- */}
            <PageErrorBoundary pageName="Dashboard">
              <div style={effectiveView !== 'dashboard' ? { display: 'none' } : undefined}>
                <Dashboard />
              </div>
            </PageErrorBoundary>
            <PageErrorBoundary pageName="Positions">
              <div style={effectiveView !== 'purchases' ? { display: 'none' } : undefined}>
                <PurchaseLog />
              </div>
            </PageErrorBoundary>

            {/* ---- lazy pages: mount on first visit, keep mounted ---- */}
            <PageErrorBoundary pageName="Income">
              <div style={effectiveView !== 'salaryPlan' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('salaryPlan') ? <SalaryPlan /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            <PageErrorBoundary pageName="LoopLoan">
              <div style={effectiveView !== 'loopLoan' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('loopLoan') ? <LoopLoan /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            <PageErrorBoundary pageName="Carry">
              <div style={effectiveView !== 'carry' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('carry') ? <CarryRetirement /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            <PageErrorBoundary pageName="Security">
              <div style={effectiveView !== 'security' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('security') ? <Security /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            {/* Patch BR: 'powerlaw' is normalized to 'assumptions' via
                effectiveView above, so both legacy and current views share
                the same mounted instance of Assumptions. */}
            <PageErrorBoundary pageName="Assumptions">
              <div style={effectiveView !== 'assumptions' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('assumptions') ? <Assumptions /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            {/* PRISM: paul-only. The whole block is gated so non-paul tenants
                never mount it even if a 'prism' view slips through. */}
            {isPaul ? (
              <PageErrorBoundary pageName="PRISM">
                <div style={effectiveView !== 'prism' ? { display: 'none' } : undefined}>
                  <Suspense fallback={<PageFallback />}>
                    {mountedViewsRef.current.has('prism') ? <Prism /> : null}
                  </Suspense>
                </div>
              </PageErrorBoundary>
            ) : null}
            <PageErrorBoundary pageName="Relocate">
              <div style={effectiveView !== 'relocate' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('relocate') ? <Relocate /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            <PageErrorBoundary pageName="News">
              <div style={effectiveView !== 'news' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('news') ? <News /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            <PageErrorBoundary pageName="My Plan">
              <div style={effectiveView !== 'myPlan' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('myPlan') ? <MyPlan /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
            {/* Patch AM: AdminPanel gated by isAdmin. mountedViewsRef adds
                'admin' only when isAdmin is true (above), so the component
                never mounts for standard users even if view briefly hits
                'admin' before the redirect useEffect runs. */}
            <PageErrorBoundary pageName="Admin">
              <div style={effectiveView !== 'admin' ? { display: 'none' } : undefined}>
                <Suspense fallback={<PageFallback />}>
                  {mountedViewsRef.current.has('admin') ? <AdminPanel /> : null}
                </Suspense>
              </div>
            </PageErrorBoundary>
          </main>

        <footer className="app-footer">
          {/* Patch CM: disclaimers callout moved out of the right-side
              ghost-button cluster (where it read like another minor
              action) into a left-aligned footnote so it reads as a
              notice on every tab. The whole sentence is clickable,
              "disclaimers" is the visible affordance. Hash + setView
              wiring identical to CL: hash gets set first so the
              Assumptions mount-effect picks it up and scrolls. */}
          <button
            type="button"
            className="ghost"
            style={{
              fontSize: 'var(--fs-micro)',
              color: 'var(--text-soft)',
              padding: '2px 4px',
              textAlign: 'left',
              cursor: 'pointer',
            }}
            onClick={() => {
              window.location.hash = 'disclaimers';
              setView('assumptions');
            }}
            title="Investment, tax, leverage, and affiliate disclosures"
          >
            Not investment advice. <span style={{ textDecoration: 'underline' }}>See disclaimers.</span>
          </button>
          <span style={{ display: 'flex', gap: 'var(--gap-s)', marginLeft: 'auto', alignItems: 'center' }}>
            <button
              className="ghost"
              style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)' }}
              onClick={() => exportToJson()}
              title="Download all data as JSON"
            >
              export
            </button>
            <button
              className="ghost"
              style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)' }}
              onClick={handleImport}
              title="Import a previously-exported JSON file"
            >
              import
            </button>
            {/* Patch AG: "reset to seed" only makes sense for accounts that have
                a seed in the first place. Hidden for non-super-admin so other
                users can't trip into Paul's data via the footer. The noAuth
                branch keeps it visible for local dev without cloud configured. */}
            {canSeedReset ? (
              <button
                className="ghost"
                style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)' }}
                onClick={() => {
                  if (confirm('Reset all data to workbook seed? This will overwrite any local edits.')) {
                    void resetToSeed();
                  }
                }}
              >
                reset to seed
              </button>
            ) : null}
          </span>
        </footer>
        </div>
      </AuthGate>
    </>
  );
}
